import { useCallback, useEffect, useMemo, useState } from 'react';
import { Chip } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { getMyPendingRequests } from './functions/get_my_pending_requests.function';
import { enrichPendingRequestsWithUsers } from './functions/enrich_pending_requests_with_users.function';
import { getAllUsers } from '../SystemRoleAssignment/functions/get_all_users.function';
import { getAllRoles } from '../SystemRoleAssignment/functions/get_all_roles.function';
import styles from '../RoleAssignment/role_assignment.module.css';
import { getRoleAssignmentTableBaseOptions } from '../RoleAssignment/role_assignment_table.utils';
import { RoleAssignmentTableShell } from '../RoleAssignment/role_assignment_table_shell.component';
import { RoleAssignmentSearchToolbar } from '../RoleAssignment/role_assignment_search_toolbar.component';

const actionColor = {
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'error',
};

const cellText = (value, muted = false) => (
  <span style={{ fontSize: '14px', color: muted ? '#667085' : '#1D242E' }}>{value || 'N/A'}</span>
);

export const SystemInvoiceApproverMyPending = ({ refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [pendingData, usersResult, rolesResult] = await Promise.all([
      getMyPendingRequests(),
      getAllUsers(),
      getAllRoles(),
    ]);
    const users = usersResult?.response || usersResult?.data || [];
    const roles = rolesResult?.response || rolesResult?.data || [];
    const enriched = enrichPendingRequestsWithUsers(
      Array.isArray(pendingData) ? pendingData : [],
      users,
      roles
    );
    setRows(enriched);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const filteredRows = useMemo(() => {
    if (!searchTerm) {
      return rows;
    }
    const q = searchTerm.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((val) =>
        String(val ?? '')
          .toLowerCase()
          .includes(q)
      )
    );
  }, [rows, searchTerm]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'action_type',
        header: 'Action',
        size: 120,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            color={actionColor[cell.getValue()] || 'default'}
          />
        ),
      },
      {
        accessorKey: 'role_name',
        header: 'Levels',
        size: 200,
        enableColumnActions: false,
        Cell: ({ cell }) => cellText(cell.getValue()),
      },
      {
        accessorKey: 'user_name',
        header: 'Name',
        size: 150,
        enableColumnActions: false,
        Cell: ({ cell }) => cellText(cell.getValue()),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 160,
        enableColumnActions: false,
        Cell: () => <Chip label="Pending approval" size="small" color="warning" />,
      },
      {
        accessorKey: 'requested_at',
        header: 'Submitted',
        size: 180,
        enableColumnActions: false,
        Cell: ({ cell }) =>
          cellText(cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '—', true),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    ...getRoleAssignmentTableBaseOptions(),
    columns,
    data: filteredRows,
    state: { isLoading: loading },
    enableSorting: false,
    renderTopToolbarCustomActions: () => (
      <RoleAssignmentSearchToolbar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
    ),
  });

  if (!loading && rows.length === 0) {
    return null;
  }

  return (
    <div
      className={`${styles['role-assignment-container']} ${styles['invoice-approver-section']}`}
      style={{ padding: 0 }}
    >
      <h2 className={styles['invoice-approver-section-title']}>Your pending submissions</h2>
      <RoleAssignmentTableShell>
        <MaterialReactTable table={table} />
      </RoleAssignmentTableShell>
    </div>
  );
};
